import React, { useState, useMemo, useEffect } from 'react';
import { Box } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import TeamMembersNavbar from 'team/components/members/TeamMembersNavbar';
import TeamMembersCardContainer from 'team/components/members/TeamMembersCardContainer';
import TeamProgressUI from 'team/shared/features/progress/TeamProgressUI';
import NotificationSnackbar from 'components/common/notification/NotificationSnackbar';
import { useTeamMembers } from 'team/hooks/useTeamMembers';
import { useNotificationFromRoute } from 'team/hooks/useNotificationFromRoute';

const TeamMembersContainer = ({ user, team }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCards, setSelectedCards] = useState(new Set());
    const [currentSort, setCurrentSort] = useState('dateOldest');
    const [viewMode, setViewMode] = useState('grid');
    const [filters, setFilters] = useState({
        subteams: [],
        templates: [],
        adminStatus: [], // 'admin', 'member'
        assignments: [], // 'assigned', 'unassigned'
        accessoryStatus: []
    });
    const [filterSearch, setFilterSearch] = useState('');
    const navigate = useNavigate();
    const location = useLocation();

    // Fetch and filter team members
    const { members, loading } = useTeamMembers(team);

    // Handle notifications from route
    const { notification, handleNotificationClose } = useNotificationFromRoute();

    const handleCreateCardClick = () => {
        navigate('/team/add-members');
    };

    const handleEditCardClick = (member) => {
        // Check if member has a default card
        if (!member.defaultCard || !member.defaultCard._id) {
            return;
        }

        // Navigate to the card ID instead of member ID
        navigate(`/team/members/card/${member.defaultCard._id}`);
    };

    const handleSearch = (query) => {
        setSearchQuery(query);
    };

    const handleCardSelection = (cardId, isSelected) => {
        setSelectedCards(prev => {
            const newSet = new Set(prev);
            if (isSelected) {
                newSet.add(cardId);
            } else {
                newSet.delete(cardId);
            }
            return newSet;
        });
    };

    const handleAddMemberSelect = () => {
        navigate('/team/add-members');
    };

    const handleSortChange = (sortValue) => {
        setCurrentSort(sortValue);
    };

    const handleViewModeChange = (mode) => {
        setViewMode(mode);
    };

    const handleFilterChange = (filterType, value) => {
        setFilters(prev => ({
            ...prev,
            [filterType]: value
        }));
    };

    const handleFilterSearchChange = (searchValue) => {
        setFilterSearch(searchValue);
    };

    const handleClearFilters = () => {
        setFilters({
            subteams: [],
            templates: [],
            adminStatus: [],
            assignments: [],
            accessoryStatus: []
        });
        setFilterSearch('');
    };

    useEffect(() => {
        const savedViewMode = window.localStorage.getItem('teamMembersViewMode');
        if (savedViewMode === 'list' || savedViewMode === 'grid') {
            setViewMode(savedViewMode);
        }
    }, []);

    useEffect(() => {
        const navEntry = performance.getEntriesByType('navigation')[0];
        const isReload = navEntry?.type === 'reload';
        if (!isReload) return;

        const params = new URLSearchParams(window.location.search);
        if (params.has('subteamId')) {
            params.delete('subteamId');
            const nextSearch = params.toString();
            navigate(
                {
                    pathname: window.location.pathname,
                    search: nextSearch ? `?${nextSearch}` : ''
                },
                { replace: true }
            );
        }
    }, [navigate]);

    useEffect(() => {
        window.localStorage.setItem('teamMembersViewMode', viewMode);
    }, [viewMode]);

    // Helper function to get member name for sorting
    const getMemberName = (member) => {
        return (
            member.defaultCard?.contact_info?.fullName ||
            member.defaultCard?.cardName ||
            member.user?.fullName ||
            member.contact_info?.fullName ||
            member.cardName ||
            member.fullName ||
            'Unknown User'
        ).toLowerCase().trim();
    };

    // Helper function to get member date for sorting (prefer createdAt, fallback to joinedAt)
    const getMemberDate = (member) => {
        const date = member.createdAt || member.joinedAt || member.created_at || member.joined_at;
        return date ? new Date(date) : new Date(0);
    };

    const getIdString = (value) => {
        if (!value) return null;
        if (typeof value === 'string') return value;
        if (value._id) return String(value._id);
        return null;
    };

    // Apply filtering to members
    const filteredMembers = useMemo(() => {
        if (!members || members.length === 0) return [];

        return members.filter(member => {
            // Filter by Admin Status
            if (filters.adminStatus && filters.adminStatus.length > 0) {
                let matchesStatus = false;
                if (filters.adminStatus.includes('fullTeamAdmin') && member.role === 'admin') matchesStatus = true;
                if (filters.adminStatus.includes('subteamAdmin') && member.isSubteamAdmin) matchesStatus = true;
                if (filters.adminStatus.includes('nonAdmin') && member.role !== 'admin' && !member.isSubteamAdmin) matchesStatus = true;
                if (!matchesStatus) return false;
            }

            // Filter by Assignments
            if (filters.assignments && filters.assignments.length > 0) {
                let matchesAssignment = false;
                if (filters.assignments.includes('assignedToSubteam') && member.subteamIds?.length > 0) matchesAssignment = true;
                if (filters.assignments.includes('notAssignedToSubteam') && (!member.subteamIds || member.subteamIds.length === 0)) matchesAssignment = true;
                if (filters.assignments.includes('assignedToTemplate') && !!member.defaultCard?.appliedTemplateId) matchesAssignment = true;
                if (!matchesAssignment) return false;
            }

            // Filter by Accessory Activation
            if (filters.accessoryStatus && filters.accessoryStatus.length > 0) {
                let matchesAccessory = false;
                if (filters.accessoryStatus.includes('accessoryActivated') && member.hasActiveAccessory === true) matchesAccessory = true;
                if (filters.accessoryStatus.includes('noAccessoryActivated') && member.hasActiveAccessory === false) matchesAccessory = true;
                if (!matchesAccessory) return false;
            }

            // Filter by Subteams
            if (filters.subteams && filters.subteams.length > 0) {
                const memberSubteamIds = (member.subteamIds || []).map(id => getIdString(id));
                const selectedSubteamIds = filters.subteams.map(id => getIdString(id));
                const matchesSubteam = selectedSubteamIds.some(id => memberSubteamIds.includes(id));
                if (!matchesSubteam) return false;
            }

            // Filter by Templates
            if (filters.templates && filters.templates.length > 0) {
                const appliedTemplateId = getIdString(member.defaultCard?.appliedTemplateId);
                const selectedTemplateIds = filters.templates.map(id => getIdString(id));
                if (!appliedTemplateId || !selectedTemplateIds.includes(appliedTemplateId)) return false;
            }

            return true;
        });
    }, [members, filters]);

    // Apply sorting to members
    const sortedMembers = useMemo(() => {
        if (!currentSort || filteredMembers.length === 0) {
            return filteredMembers;
        }

        const sorted = [...filteredMembers];

        switch (currentSort) {
            case 'dateNewest':
                // Sort by createdAt/joinedAt descending (newest first)
                sorted.sort((a, b) => {
                    const dateA = getMemberDate(a);
                    const dateB = getMemberDate(b);
                    return dateB.getTime() - dateA.getTime();
                });
                break;

            case 'dateOldest':
                // Sort by createdAt/joinedAt ascending (oldest first)
                sorted.sort((a, b) => {
                    const dateA = getMemberDate(a);
                    const dateB = getMemberDate(b);
                    return dateA.getTime() - dateB.getTime();
                });
                break;

            case 'alphabeticalAZ':
                // Sort alphabetically A-Z by name
                sorted.sort((a, b) => {
                    const nameA = getMemberName(a);
                    const nameB = getMemberName(b);
                    return nameA.localeCompare(nameB);
                });
                break;

            case 'alphabeticalZA':
                // Sort alphabetically Z-A by name
                sorted.sort((a, b) => {
                    const nameA = getMemberName(a);
                    const nameB = getMemberName(b);
                    return nameB.localeCompare(nameA);
                });
                break;

            default:
                return filteredMembers;
        }

        return sorted;
    }, [filteredMembers, currentSort]);

    return (
        <div>
            <TeamMembersNavbar
                onSearch={handleSearch}
                membersCount={filteredMembers.length}
                onAddMemberSelect={handleAddMemberSelect}
                currentSort={currentSort}
                onSortChange={handleSortChange}
                viewMode={viewMode}
                onViewModeChange={handleViewModeChange}
                filters={filters}
                filterSearch={filterSearch}
                onFilterChange={handleFilterChange}
                onFilterSearchChange={handleFilterSearchChange}
                onClearFilters={handleClearFilters}
            />

            <TeamProgressUI />

            <Box sx={{ marginBottom: '2rem' }} />

            <TeamMembersCardContainer
                items={sortedMembers}
                searchQuery={searchQuery}
                selectedCards={selectedCards}
                onCardSelection={handleCardSelection}
                onEditCardClick={handleEditCardClick}
                onCreateCardClick={handleCreateCardClick}
                teamId={team?._id}
                viewMode={viewMode}
            />

            <NotificationSnackbar
                open={notification.open}
                message={notification.message}
                handleClose={handleNotificationClose}
                severity={notification.severity}
            />
        </div>
    );
};

export default TeamMembersContainer;
